import { useState, useEffect } from 'react';
import { offerStoreAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';

export function UploadOffersModal({ open, onClose, onCommitted }) {
  const [vendors, setVendors] = useState([]);
  const [vendorMode, setVendorMode] = useState('existing');
  const [vendorZohoId, setVendorZohoId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [offerDate, setOfferDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    offerStoreAPI.getVendors().then(r => setVendors(r.data.vendors || [])).catch(() => {});
  }, []);

  const handlePreview = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    if (vendorMode === 'existing' && !vendorZohoId) { toast.error('Select a vendor'); return; }
    if (vendorMode === 'new' && !vendorName.trim()) { toast.error('Vendor name is required'); return; }
    setLoading(true);
    try {
      const r = await offerStoreAPI.uploadPreview(file, vendorMode === 'existing' ? vendorZohoId : '', vendorMode === 'new' ? vendorName : '', offerDate);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!preview?.preview_id) return;
    setCommitting(true);
    try {
      const r = await offerStoreAPI.uploadCommit(preview.preview_id);
      toast.success(r.data.message || 'Offers added');
      onCommitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to commit upload');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="upload-offers-modal">
        <DialogHeader><DialogTitle>Upload offer sheet</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-3">
            <div>
              <Label>Vendor *</Label>
              <Select value={vendorMode} onValueChange={setVendorMode}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="upload-offer-vendor-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing">Pick an existing vendor</SelectItem>
                  <SelectItem value="new">Type a new vendor name</SelectItem>
                </SelectContent>
              </Select>
              {vendorMode === 'existing' ? (
                <Select value={vendorZohoId} onValueChange={setVendorZohoId}>
                  <SelectTrigger className="rounded-sm mt-2" data-testid="upload-offer-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => <SelectItem key={v.zoho_vendor_id} value={v.zoho_vendor_id}>{v.vendor_name}{v.country ? ` (${v.country})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={vendorName} onChange={e => setVendorName(e.target.value)} placeholder="Vendor name" className="rounded-sm mt-2" data-testid="upload-offer-vendor-name-input" />
              )}
            </div>
            <div><Label>Offer date</Label><Input type="date" value={offerDate} onChange={e => setOfferDate(e.target.value)} className="rounded-sm mt-1" data-testid="upload-offer-date-input" /></div>
            <div>
              <Label>Excel file (.xlsx)</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} className="rounded-sm mt-1" data-testid="upload-offer-file-input" />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{preview.row_count} row(s) found for <span className="font-medium">{preview.vendor_name}</span>. Review before adding.</p>
            <div className="max-h-[360px] overflow-y-auto border rounded-sm">
              <Table>
                <TableHeader><TableRow><TableHead>Model</TableHead><TableHead>Qty</TableHead><TableHead>Price</TableHead><TableHead>Availability</TableHead><TableHead>SKU match</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.rows.map(r => (
                    <TableRow key={r.row_num} data-testid={`upload-preview-row-${r.row_num}`}>
                      <TableCell className="text-sm">{r.model_raw}</TableCell>
                      <TableCell className="text-sm">{r.qty}</TableCell>
                      <TableCell className="text-sm">{r.price}</TableCell>
                      <TableCell className="text-sm">{r.availability === 'LEAD_TIME' ? `Lead time ${r.lead_time_days ?? ''}d` : 'Ready stock'}</TableCell>
                      <TableCell className="text-sm">{r.matched_sku ? <Badge variant="outline" className="rounded-sm text-xs">{r.matched_sku}</Badge> : <span className="text-muted-foreground">Not in catalogue</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="upload-offer-cancel-btn">Cancel</Button>
          {!preview ? (
            <Button className="rounded-sm" onClick={handlePreview} disabled={loading} data-testid="upload-offer-preview-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Preview
            </Button>
          ) : (
            <Button className="rounded-sm" onClick={handleCommit} disabled={committing} data-testid="upload-offer-commit-btn">
              {committing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Add {preview.row_count} offer(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
